// ─── Google Maps Service ──────────────────────────────────────────────────────
// Handles: Maps JS API loading, stop geocoding, route directions, place details
// Used by: JourneyScreen map component

export interface LatLng {
  lat: number
  lng: number
}

export interface MapsStop {
  name: string
  position: LatLng
  placeId?: string
  address?: string
  travelTimeFromPrev?: string   // e.g. "12 mins"
  distanceFromPrev?: string     // e.g. "4.2 km"
  // ── InfoWindow enrichment (optional) ──
  imageUrl?: string
  timeLabel?: string            // e.g. "Day 1" / "Dawn"
}

export interface RouteResult {
  stops: MapsStop[]
  totalDuration: string
  totalDistance: string
}

// ─── Known stop coordinates (curated, avoids geocoding costs for fixed data) ──
// In Phase 2, these will come from the Places API dynamically
const CURATED_COORDS: Record<string, LatLng> = {
  // Orlando
  'magic kingdom':         { lat: 28.4177, lng: -81.5812 },
  'epcot':                 { lat: 28.3747, lng: -81.5494 },
  'hollywood studios':     { lat: 28.3575, lng: -81.5583 },
  'animal kingdom':        { lat: 28.3553, lng: -81.5900 },
  'legoland florida':      { lat: 28.0053, lng: -81.8072 },
  // Singapore
  'universal studios singapore': { lat: 1.2540, lng: 103.8238 },
  'gardens by the bay':    { lat: 1.2816, lng: 103.8636 },
  'adventure cove waterpark': { lat: 1.2531, lng: 103.8230 },
  'singapore zoo':         { lat: 1.4043, lng: 103.7930 },
  'sea aquarium':          { lat: 1.2538, lng: 103.8193 },
  // Dubai
  'wild wadi waterpark':   { lat: 25.1418, lng: 55.1853 },
  'desert safari dubai':   { lat: 24.9857, lng: 55.4272 },
  'ski dubai':             { lat: 25.1183, lng: 55.2003 },
  'dubai frame':           { lat: 25.2353, lng: 55.3004 },
  'burj khalifa':          { lat: 25.1972, lng: 55.2744 },
  // Kenya
  'masai mara':            { lat: -1.5021, lng: 35.1438 },
  'giraffe centre nairobi':{ lat: -1.3749, lng: 36.7508 },
  'david sheldrick elephant orphanage': { lat: -1.3784, lng: 36.7508 },
  'masai mara balloon':    { lat: -1.5050, lng: 35.1600 },
  'maasai village':        { lat: -1.5100, lng: 35.1700 },
  // Costa Rica
  'arenal volcano':        { lat: 10.4630, lng: -84.7032 },
  'monteverde':            { lat: 10.3052, lng: -84.8192 },
  'sloth sanctuary':       { lat: 10.1975, lng: -83.8053 },
  'manuel antonio':        { lat: 9.3868, lng: -84.1408 },
  // New Zealand
  'waitomo glowworm caves':{ lat: -38.2620, lng: 175.1047 },
  'hobbiton movie set':    { lat: -37.8578, lng: 175.6820 },
  'queenstown':            { lat: -45.0312, lng: 168.6626 },
  'milford sound':         { lat: -44.6413, lng: 167.8972 },
  // Mallorca
  'playa de muro':         { lat: 39.8030, lng: 3.1110 },
  'western water park mallorca': { lat: 39.5990, lng: 2.4530 },
  "port d'alcudia":        { lat: 39.8530, lng: 3.1240 },
  'caves of drach':        { lat: 39.5330, lng: 3.3410 },
  'palma old town':        { lat: 39.5696, lng: 2.6502 },
  // Tenerife
  'siam park':             { lat: 28.0726, lng: -16.7346 },
  'tenerife whale watching':{ lat: 28.0600, lng: -16.7200 },
  'teide national park':   { lat: 28.2723, lng: -16.6424 },
  'loro parque':           { lat: 28.4152, lng: -16.5505 },
  'playa las teresitas':   { lat: 28.5098, lng: -16.1774 },
  // Bali
  'waterbom bali':         { lat: -8.7213, lng: 115.1697 },
  'sacred monkey forest ubud': { lat: -8.5190, lng: 115.2594 },
  'kuta beach bali':       { lat: -8.7184, lng: 115.1686 },
  'tegalalang rice terraces': { lat: -8.4314, lng: 115.2789 },
  'tirta empul':           { lat: -8.4149, lng: 115.3149 },
  // Japan
  'teamlab planets tokyo': { lat: 35.6464, lng: 139.7854 },
  'kyoto station':         { lat: 34.9858, lng: 135.7588 },
  'arashiyama bamboo grove': { lat: 35.0094, lng: 135.6722 },
  'osaka aquarium kaiyukan': { lat: 34.6550, lng: 135.4289 },
  'nara deer park':        { lat: 34.6851, lng: 135.8050 },
  // Portugal
  'pena palace sintra':    { lat: 38.7877, lng: -9.3904 },
  'cascais beach':         { lat: 38.6969, lng: -9.4209 },
  'lisbon oceanarium':     { lat: 38.7637, lng: -9.0931 },
  'quinta da regaleira':   { lat: 38.7973, lng: -9.3944 },
  'belem tower':           { lat: 38.6916, lng: -9.2160 },
  // Rome
  'colosseum rome':        { lat: 41.8902, lng: 12.4922 },
  'vatican museums':       { lat: 41.9065, lng: 12.4536 },
  'borghese gardens rome': { lat: 41.9148, lng: 12.4924 },
  'piazza navona rome':    { lat: 41.8992, lng: 12.4730 },
  'trevi fountain':        { lat: 41.9009, lng: 12.4833 },
}

function findCoords(stopName: string): LatLng | null {
  const key = stopName
    .toLowerCase()
    .replace(/[🎢🦁🏖🏯🌿🏔🌆🏙🌴🌺🛡🎋🦌🐠🌌🚅🏴‍☠️🦇🏛💧🌊🐒🏄📸🙏🐬🐋🧝🎈🌍🎁✨⭐🚀💨🐘🌋⚡🐒🔦🥧🚣🍝🍦🎢🐢]/gu, '')
    .replace(/[^\w\s]/g, '')
    .trim()
    .split(' ').slice(0, 4).join(' ')

  // Try progressively shorter matches
  for (const [k, v] of Object.entries(CURATED_COORDS)) {
    if (key.includes(k) || k.includes(key.split(' ').slice(0, 2).join(' '))) return v
  }
  return null
}

// ─── Maps JS API Loader ────────────────────────────────────────────────────────

let mapsPromise: Promise<typeof google.maps> | null = null

export function loadGoogleMaps(): Promise<typeof google.maps> {
  if (mapsPromise) return mapsPromise

  mapsPromise = new Promise((resolve, reject) => {
    // Key resolution: build-time env first (local dev), then runtime config
    // served by the Pages worker (production — dashboard-managed, referrer-restricted).
    const resolveKey = async (): Promise<string> => {
      const buildKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY
      if (buildKey && buildKey !== 'AIza_your_maps_key_here') return buildKey
      const res = await fetch('/api/config')
      if (!res.ok) throw new Error('config unavailable')
      const cfg = await res.json()
      if (!cfg.mapsKey) throw new Error('Google Maps API key not configured')
      return cfg.mapsKey as string
    }

    if (window.google?.maps) {
      resolve(window.google.maps)
      return
    }

    resolveKey().then((key) => {
    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`
    script.async = true
    script.defer = true
    script.onload = () => resolve(window.google.maps)
    script.onerror = () => reject(new Error('Google Maps script failed to load'))
    document.head.appendChild(script)
    }).catch(reject)
  })

  return mapsPromise
}

// ─── Initialise a map on a container element ──────────────────────────────────

export async function initMap(
  container: HTMLElement,
  center: LatLng,
  zoom = 11
): Promise<google.maps.Map> {
  const maps = await loadGoogleMaps()
  return new maps.Map(container, {
    center,
    zoom,
    mapId: 'yonder_family_map',
    disableDefaultUI: true,
    zoomControl: true,
    styles: [
      { elementType: 'geometry', stylers: [{ color: '#241a42' }] },
      { elementType: 'labels.text.stroke', stylers: [{ color: '#140F2B' }] },
      { elementType: 'labels.text.fill', stylers: [{ color: '#c9bfe0' }] },
      { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
      { featureType: 'transit', stylers: [{ visibility: 'off' }] },
      { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#1b2a4a' }] },
      { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#2a1b45' }] },
      { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#3d2452' }] },
      { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#ffd68c' }] },
    ],
  })
}

// ─── Place numbered markers ───────────────────────────────────────────────────

function buildInfoWindowContent(stop: MapsStop): string {
  const safeName = escapeHtml(stop.name)
  const safeLabel = stop.timeLabel ? escapeHtml(stop.timeLabel) : ''
  const imgHtml = stop.imageUrl
    ? `<div style="width:100%;height:88px;border-radius:10px;overflow:hidden;margin-bottom:8px;background:#241a42;">
         <img src="${escapeHtml(stop.imageUrl)}" alt="${safeName}" style="width:100%;height:100%;object-fit:cover;display:block;" />
       </div>`
    : ''
  return `
    <div style="
      font-family:'Nunito',sans-serif;
      background:linear-gradient(180deg,#2A1B45,#1B1436);
      border:1.5px solid #FF9500;
      border-radius:14px;
      padding:10px;
      min-width:160px;
      max-width:200px;
      box-shadow:0 8px 24px rgba(0,0,0,0.35);
    ">
      ${imgHtml}
      ${safeLabel ? `<div style="font-size:9px;font-weight:800;letter-spacing:0.06em;text-transform:uppercase;color:#FFD166;margin-bottom:2px;">${safeLabel}</div>` : ''}
      <div style="font-size:12px;font-weight:800;color:#FFF7EC;line-height:1.25;">${safeName}</div>
    </div>
  `
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

export async function placeMarkers(
  map: google.maps.Map,
  stops: MapsStop[],
  onStopClick?: (index: number) => void
): Promise<google.maps.Marker[]> {
  const maps = await loadGoogleMaps()
  // Teardrop pin path (origin at the point's tip, drawn upward) — Yonder sunset-orange.
  const TEARDROP_PATH =
    'M0,0 C-9,-18 -14,-26 -14,-34 C-14,-45.05 -7.73,-52 0,-52 C7.73,-52 14,-45.05 14,-34 C14,-26 9,-18 0,0 Z'

  let openInfoWindow: google.maps.InfoWindow | null = null

  return stops.map((stop, i) => {
    const marker = new maps.Marker({
      position: stop.position,
      map,
      title: stop.name,
      label: {
        text: String(i + 1),
        color: '#FFFFFF',
        fontWeight: 'bold',
        fontSize: '12px',
        fontFamily: 'Nunito, sans-serif',
      },
      icon: {
        path: TEARDROP_PATH,
        fillColor: '#FF9500',
        fillOpacity: 1,
        strokeColor: '#FFD166',
        strokeWeight: 1.5,
        scale: 0.9,
        labelOrigin: new maps.Point(0, -34),
        anchor: new maps.Point(0, 0),
      },
    })

    const infoWindow = new maps.InfoWindow({
      content: buildInfoWindowContent(stop),
      pixelOffset: new maps.Size(0, -10),
    })

    marker.addListener('click', () => {
      if (openInfoWindow) openInfoWindow.close()
      infoWindow.open({ map, anchor: marker })
      openInfoWindow = infoWindow
      onStopClick?.(i)
    })

    return marker
  })
}

// ─── Draw route polyline between stops ───────────────────────────────────────

export async function drawRoute(
  map: google.maps.Map,
  stops: MapsStop[]
): Promise<void> {
  const maps = await loadGoogleMaps()
  const path = stops.map((s) => s.position)
  new maps.Polyline({
    path,
    geodesic: true,
    strokeColor: '#FF6B2B',
    strokeOpacity: 0.7,
    strokeWeight: 3,
    icons: [{
      icon: { path: maps.SymbolPath.FORWARD_OPEN_ARROW, scale: 3, strokeColor: '#FF9500' },
      offset: '50%',
      repeat: '120px',
    }],
    map,
  })
}

// ─── Fit map bounds to all stops ──────────────────────────────────────────────

export async function fitBounds(
  map: google.maps.Map,
  stops: MapsStop[]
): Promise<void> {
  const maps = await loadGoogleMaps()
  const bounds = new maps.LatLngBounds()
  stops.forEach((s) => bounds.extend(s.position))
  map.fitBounds(bounds, { top: 40, right: 20, bottom: 40, left: 20 })
}

// ─── Resolve stop coordinates (curated first, geocode fallback) ───────────────

export async function resolveStopCoordinates(stopNames: string[]): Promise<MapsStop[]> {
  const results: MapsStop[] = []

  for (const name of stopNames) {
    const curated = findCoords(name)
    if (curated) {
      results.push({ name, position: curated })
      continue
    }

    // Geocode fallback via Maps Geocoding API
    try {
      const maps = await loadGoogleMaps()
      const geocoder = new maps.Geocoder()
      const response = await geocoder.geocode({ address: name })
      if (response.results.length > 0) {
        const loc = response.results[0].geometry.location
        results.push({
          name,
          position: { lat: loc.lat(), lng: loc.lng() },
          address: response.results[0].formatted_address,
          placeId: response.results[0].place_id,
        })
      }
    } catch {
      console.warn(`Could not geocode stop: ${name}`)
    }
  }

  return results
}

// ─── Get travel times between consecutive stops via Directions API ────────────

export async function getTravelTimes(stops: MapsStop[]): Promise<MapsStop[]> {
  if (stops.length < 2) return stops

  try {
    const maps = await loadGoogleMaps()
    const service = new maps.DirectionsService()
    const waypoints = stops.slice(1, -1).map((s) => ({ location: s.position, stopover: true }))

    const result = await service.route({
      origin: stops[0].position,
      destination: stops[stops.length - 1].position,
      waypoints,
      travelMode: maps.TravelMode.DRIVING,
    })

    const legs = result.routes[0]?.legs ?? []
    return stops.map((stop, i) => ({
      ...stop,
      travelTimeFromPrev: i > 0 ? legs[i - 1]?.duration?.text : undefined,
      distanceFromPrev: i > 0 ? legs[i - 1]?.distance?.text : undefined,
    }))
  } catch {
    return stops
  }
}
